import React from 'react'
import { useTranslation } from 'react-i18next'
import { parsePredecessors } from '../../lib/predecessors'
import type { Tree } from '../../lib/tree'
import type { ScheduleActivity } from '../../types'
import CellEditor, { type CellMove } from './CellEditor'

interface Props {
  activityId: string
  value: string
  tree: Tree<ScheduleActivity>
  onCommit: (v: string, move: CellMove) => void
  onCancel: () => void
}

/**
 * The `predecessors` cell (spec B §9.4): MS Project-style token syntax (`12`, `12SS+2d`, `7, 9SF+3d`),
 * validated locally against the tree via `parsePredecessors`. The commit sequence (diff → delete →
 * update → create) lives in `useCellEditing`'s `commitPredecessors` — this editor only turns the typed
 * text into a pass/fail with the offending token, same contract as every other cell editor.
 */
const PredecessorsCell: React.FC<Props> = ({ activityId, value, tree, onCommit, onCancel }) => {
  const { t } = useTranslation()
  return (
    <CellEditor
      initial={value}
      validate={(v) => {
        const r = parsePredecessors(v, tree, activityId)
        // `r.ok === true` rather than a truthy check: with strictNullChecks off (this project's
        // tsconfig), TS does not narrow a `{ ok: true } | { ok: false }` union on `r.ok` / `!r.ok`
        // — only on an explicit literal comparison — so a bare ternary here would leave `r.error`
        // and `r.token` below type-checking against the whole union.
        if (r.ok === true) return null
        return t(`Schedule_pred_error_${r.error}`, { token: r.token })
      }}
      onCommit={onCommit}
      onCancel={onCancel}
    />
  )
}

export default PredecessorsCell
